/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import api from '../../api/api';
import Loading from '../../components/Loading/Loading';
import { buildQueryParams } from '../../utils/helpers';
import './Reorder.sass';

export default function Reorder({ offer }) {
  const history = useHistory();
  const location = useLocation();
  const queryParams = buildQueryParams(location, true);
  const { email, orderId: orderIdCamel, orderid: orderIdLower } = queryParams;
  const orderId = orderIdCamel || orderIdLower;

  const [orderData, setOrderData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [calculatedTotal, setCalculatedTotal] = useState(null);
  const [totalLoading, setTotalLoading] = useState(false);

  useEffect(() => {
    if (!orderId) {
      setError('Order ID is required to load your order');
      setLoading(false);
      return;
    }

    fetchLastOrder();
  }, [orderId]);

  const fetchLastOrder = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = { orderId };
      if (email) {
        params.email = email;
      }

      const response = await api.get('/reorder/last-order', {
        params,
        validateStatus: function (status) {
          return status < 500;
        },
      });

      if (response.status === 200) {
        setOrderData(response.data);
        calculateTotal(response.data);
      } else if (response.status === 404) {
        setError("We couldn't find this order. Please check your order number or contact support.");
      } else if (response.status === 403) {
        setError("This order doesn't match the provided email address.");
      } else if (response.data?.error) {
        setError(response.data.error);
      } else {
        setError('Unable to load your order. Please try again or contact support.');
      }
    } catch (err) {
      setError('Unable to load your order. Please try again or contact support.');
    } finally {
      setLoading(false);
    }
  };

  const calculateTotal = async (orderData) => {
    try {
      setTotalLoading(true);

      const payload = {
        orderId: orderData.stickyOrderId,
        products: orderData.products,
        campaignId: orderData.campaignId,
        shippingId: orderData.shippingId,
        shippingAddress: {
          state: orderData.shippingState,
          zip: orderData.shippingZip,
          country: orderData.shippingCountry,
        },
      };

      const response = await api.post('/reorder/calculate-total', payload);

      if (response.status === 200) {
        setCalculatedTotal(response.data);
      }
    } catch (err) {
      setCalculatedTotal(null);
    } finally {
      setTotalLoading(false);
    }
  };

  const handleConfirmReorder = async () => {
    if (!orderData) {
      return;
    }

    try {
      setProcessing(true);

      const payload = {
        email: orderData.email,
        stickyOrderId: orderData.stickyOrderId,
      };

      const response = await api.post('/reorder/create', payload);

      if (response.data.success) {
        trackReorderEvent(orderData, response.data);
        history.push(`/thank-you?internalorderid=${response.data.mongoOrderId}`);
      }
    } catch (err) {
      setError(
        err.response?.data?.error || 'Unable to process your order. Please try again or contact support.'
      );
      setProcessing(false);
    }
  };

  const trackReorderEvent = (orderData, response) => {
    // Track to Google Analytics if available
    if (window.dataLayer) {
      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({
        event: 'reorder_initiated',
        ecommerce: {
          transaction_id: response.orderId,
          value: orderData.total,
          currency: 'USD',
          items: orderData.products?.map((item) => ({
            item_id: item.product_id,
            item_name: item.product_name,
            price: item.product_price || item.price,
            quantity: item.product_qty || 1,
          })),
        },
      });
    }

    // Track to Facebook Pixel if available
    if (window.fbq) {
      window.fbq('track', 'Purchase', {
        value: orderData.total,
        currency: 'USD',
        content_type: 'product',
      });
    }
  };

  const getContactEmail = () => {
    return offer?.company?.email || 'support@example.com';
  };

  const getContactPhone = () => {
    return offer?.company?.phone || '';
  };

  if (loading) {
    return (
      <div className='reorder-container'>
        <div className='reorder-loading'>
          <Loading />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className='reorder-container'>
        <div className='reorder-error-container'>
          <div className='reorder-error-icon'>⚠️</div>
          <h2>Unable to Load Your Order</h2>
          <p className='reorder-error-message'>{error}</p>
          <p className='reorder-help-text'>
            If you need assistance, please contact our Customer Service team:
          </p>
          <div className='reorder-contact-info'>
            <p>
              <strong>Email:</strong> <a href={`mailto:${getContactEmail()}`}>{getContactEmail()}</a>
            </p>
            {getContactPhone() && (
              <p>
                <strong>Phone:</strong> <a href={`tel:${getContactPhone()}`}>{getContactPhone()}</a>
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (!orderData) {
    return null;
  }

  return (
    <div className='reorder-container'>
      <div className='reorder-content'>
        <h1 className='reorder-heading'>
          {orderData.firstName ? `${orderData.firstName}, Ready to Restock?` : 'Ready to Restock?'}
        </h1>
        <p className='reorder-subheading'>
          Keep the same great price and reorder your last purchase with one click.
        </p>

        <div className='reorder-products-container'>
          {orderData.products?.map((product, index) => {
            const productName = product.product_name || 'Unknown Product';
            const productPrice = product.product_price || product.price || 0;
            const productQty = product.product_qty || 1;
            const productImage = product.product_image || 'https://placehold.co/150x150';

            return (
              <div key={index} className='reorder-product-card'>
                <div className='reorder-product-image'>
                  <img
                    src={productImage}
                    alt={productName}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = 'https://placehold.co/150x150';
                    }}
                  />
                </div>
                <div className='reorder-product-info'>
                  <h3 className='reorder-product-name'>{productName}</h3>
                  {productQty > 1 && <p className='reorder-product-quantity'>Quantity: {productQty}</p>}
                  <p className='reorder-product-price'>${parseFloat(productPrice).toFixed(2)}</p>
                </div>
              </div>
            );
          })}
        </div>

        <div className='reorder-total-section'>
          <div className='reorder-breakdown-row'>
            <span className='reorder-breakdown-label'>Subtotal:</span>
            <span className='reorder-breakdown-value'>
              {totalLoading ? (
                <span className='reorder-spinner'>Loading...</span>
              ) : (
                `$${calculatedTotal?.subtotal || orderData.subtotal || '0.00'}`
              )}
            </span>
          </div>
          {totalLoading ||
          ((calculatedTotal?.shipping || orderData.shipping) &&
            parseFloat(calculatedTotal?.shipping || orderData.shipping) > 0) ? (
            <div className='reorder-breakdown-row'>
              <span className='reorder-breakdown-label'>Shipping:</span>
              <span className='reorder-breakdown-value'>
                {totalLoading ? (
                  <span className='reorder-spinner'>Loading...</span>
                ) : (
                  `$${parseFloat(calculatedTotal?.shipping || orderData.shipping).toFixed(2)}`
                )}
              </span>
            </div>
          ) : (
            <div className='reorder-breakdown-row'>
              <span className='reorder-breakdown-label'>Shipping:</span>
              <span className='reorder-breakdown-value'>FREE</span>
            </div>
          )}
          {(calculatedTotal?.tax || orderData.tax) &&
            parseFloat(calculatedTotal?.tax || orderData.tax) > 0 && (
              <div className='reorder-breakdown-row'>
                <span className='reorder-breakdown-label'>Taxes:</span>
                <span className='reorder-breakdown-value'>
                  {totalLoading ? (
                    <span className='reorder-spinner'>Loading...</span>
                  ) : (
                    `$${parseFloat(calculatedTotal?.tax || orderData.tax).toFixed(2)}`
                  )}
                </span>
              </div>
            )}
          <div className='reorder-total-row'>
            <span className='reorder-total-label'>Total:</span>
            <span className='reorder-total-amount'>
              {totalLoading ? (
                <span className='reorder-spinner'>Loading...</span>
              ) : (
                `$${parseFloat(calculatedTotal?.total || orderData.total || '0.00').toFixed(2)}`
              )}
            </span>
          </div>
        </div>

        <button className='reorder-confirm-button' onClick={handleConfirmReorder} disabled={processing}>
          {processing ? 'Processing...' : 'Confirm & Reorder'}
        </button>

        <div className='reorder-footer'>
          <p>Your payment method on file will be charged automatically.</p>
          <p>
            If you would like to make changes to your reorder, please contact our Customer Service team at{' '}
            <a href={`mailto:${getContactEmail()}`}>{getContactEmail()}</a>
            {getContactPhone() && (
              <>
                {' '}
                or <a href={`tel:${getContactPhone()}`}>{getContactPhone()}</a>
              </>
            )}
            .
          </p>
        </div>
      </div>
    </div>
  );
}
